import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import CustomDropdown from '../../../../custom/customDropdown';

const GumaScanDijalog = ({
  visible, onHide, onConfirm,
  serBroj, gumaData,
  magacinOptions, nazivArtiklaOptions,
  defaultMagacin, defaultArtikla, defaultDOT,
}) => {
  const { t } = useTranslation();
  const [idArtikla, setIdArtikla]     = useState(null);
  const [kolicina, setKolicina]       = useState(1);
  const [idMagacin, setIdMagacin]     = useState(null);
  const [kilometraza, setKilometraza] = useState(0);
  const [dot, setDot]                 = useState('');
  const [submitted, setSubmitted]     = useState(false);

  const gumaOptions = (nazivArtiklaOptions || []).filter(a => !!a.Guma);
  const isNew = !gumaData;

  // Init on every open — uses persist values from parent
  useEffect(() => {
    if (visible) {
      setSubmitted(false);
      setKolicina(1);
      setIdMagacin(defaultMagacin ?? null);
      setKilometraza(gumaData?.Kilometraza ?? 0);
      setIdArtikla(isNew ? (defaultArtikla ?? null) : null);
      setDot(isNew ? (defaultDOT ?? '') : '');
    }
  }, [visible]); // eslint-disable-line

  const getVal = (e) => (e?.target ?? e)?.value ?? e?.value;

  const handleConfirm = () => {
    setSubmitted(true);
    const artId = isNew ? idArtikla : gumaData.id_artikal;
    if (!artId || !idMagacin || kolicina == null) return;

    const artikal = (nazivArtiklaOptions || []).find(a => a.value === artId);
    onConfirm({
      NazivArtikla: artId,
      SifraStavke: artId,
      JedinicaMere: artikal?.JedinicaMere || null,
      kolicina,
      id_magacin: idMagacin,
      SerBroj: serBroj,
      Kilometraza: kilometraza ?? 0,
      DOT: dot || null,
    });
  };

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
      <Button label={t('nova.odustani')} icon="pi pi-times" onClick={onHide} className="p-button-text"
        style={{ minHeight: 44 }} />
      <Button label={t('common.ok')} icon="pi pi-check" onClick={handleConfirm} autoFocus
        style={{ minHeight: 44 }} />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={isNew ? 'Nova guma — unos podataka' : 'Guma pronađena'}
      style={{ width: '95vw', maxWidth: '480px' }}
      contentStyle={{ padding: '1rem' }}
      modal
      footer={footer}
    >
      {/* Serijski broj */}
      <div style={{ marginBottom: '1rem', padding: '0.6rem 0.8rem', background: '#f0f4ff', borderRadius: 6, borderLeft: '4px solid #3b82f6' }}>
        <span style={{ fontSize: '0.75rem', color: '#666', display: 'block', marginBottom: 2 }}>{t('magacin.serijskiBroj')}</span>
        <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1.1rem', letterSpacing: 1, color: '#1e3a8a' }}>{serBroj}</span>
      </div>

      {/* Existing tire info */}
      {gumaData && (
        <div style={{ background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: 6, padding: '0.7rem 1rem', marginBottom: '1rem', fontSize: '0.88rem', lineHeight: 2 }}>
          <div><strong>Artikal:</strong> {gumaData.NazivArtikla}{gumaData.DOT ? ` — DOT ${gumaData.DOT}` : ''}</div>
          <div><strong>Lokacija:</strong> {gumaData.Lokacija}</div>
          {gumaData.VoziloRegistracija && <div><strong>Vozilo:</strong> {gumaData.VoziloRegistracija}</div>}
          {gumaData.KlijentNaziv && <div><strong>Klijent:</strong> {gumaData.KlijentNaziv} — {gumaData.KlijentRegistracija}</div>}
          <div><strong>Trenutna km:</strong> {gumaData.Kilometraza}</div>
        </div>
      )}

      {/* Artikal + DOT — only for new tires */}
      {isNew && (
        <>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
              Artikal <span style={{ color: 'red' }}>*</span>
            </label>
            <CustomDropdown
              id="gd-artikal"
              name="NazivArtikla"
              optionLabel="label"
              value={idArtikla}
              options={gumaOptions}
              onChange={e => setIdArtikla(getVal(e))}
              placeholder={t('magacin.phArtikalGuma')}
              style={{ width: '100%' }}
              invalid={submitted && !idArtikla}
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>{t('magacin.dot')}</label>
            <InputText
              value={dot}
              onChange={e => setDot(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
              placeholder={t('magacin.phPin')}
              maxLength={4}
              style={{ width: '100%', height: '44px' }}
            />
          </div>
        </>
      )}

      {/* Magacin */}
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
          Magacin <span style={{ color: 'red' }}>*</span>
        </label>
        <CustomDropdown
          id="gd-magacin"
          name="id_magacin"
          optionLabel="label"
          value={idMagacin}
          options={magacinOptions || []}
          onChange={e => setIdMagacin(getVal(e))}
          placeholder={t('nova.phMagacin')}
          style={{ width: '100%' }}
          invalid={submitted && !idMagacin}
        />
      </div>

      {/* Kolicina + Kilometraza */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 120px' }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
            Količina <span style={{ color: 'red' }}>*</span>
          </label>
          <InputNumber
            value={kolicina}
            onValueChange={e => setKolicina(e.value)}
            min={1}
            minFractionDigits={2}
            maxFractionDigits={2}
            style={{ width: '100%' }}
            inputStyle={{ height: '44px' }}
            invalid={submitted && (kolicina == null || kolicina < 1)}
          />
        </div>
        <div style={{ flex: '1 1 120px' }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>{t('ture.kilometraza')}</label>
          <InputNumber
            value={kilometraza}
            onValueChange={e => setKilometraza(e.value ?? 0)}
            useGrouping={false}
            min={0}
            style={{ width: '100%' }}
            inputStyle={{ height: '44px' }}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default GumaScanDijalog;
